import type { TranslationKey } from '@/i18n-key';

/**
 * How much of what the administrator typed actually reached the server.
 *
 * `PARTIALLY_SAVED` is not a theoretical branch: this step writes through three different
 * endpoints, and in production the four group milestones were accepted while the three
 * launch schedules were refused. The administrator was told "an error occurred, try again later",
 * moved on, and found on reopening that three dates had reverted and a reminder box had cleared.
 * A refusal that cannot say WHAT survived is what turns a setback into silent data loss.
 */
export const DeadlinesSaveOutcome = {
    SAVED: 'saved',
    PARTIALLY_SAVED: 'partiallySaved',
    NOT_SAVED: 'notSaved',
} as const;

export type DeadlinesSaveOutcomeValue =
    (typeof DeadlinesSaveOutcome)[keyof typeof DeadlinesSaveOutcome];

/**
 * Classifies the writes this step ATTEMPTED. Only real requests belong in `results` — a milestone
 * with no row and no date is a documented no-op, and counting it as a success would report
 * "partially saved" for a save in which nothing was ever written.
 *
 * No attempt at all is `SAVED`: an administrator who changed nothing has lost nothing.
 */
export const summariseDeadlinesSave = (
    results: readonly PromiseSettledResult<unknown>[],
): DeadlinesSaveOutcomeValue => {
    const failed = results.filter((result) => result.status === 'rejected').length;

    if (failed === 0) {
        return DeadlinesSaveOutcome.SAVED;
    }

    return failed === results.length
        ? DeadlinesSaveOutcome.NOT_SAVED
        : DeadlinesSaveOutcome.PARTIALLY_SAVED;
};

/** The first refusal, handed to `handleError` so Sentry still receives the real cause. */
export const findSaveRejection = (
    results: readonly PromiseSettledResult<unknown>[],
): unknown | undefined =>
    results.find((result): result is PromiseRejectedResult => result.status === 'rejected')?.reason;

/**
 * What the administrator reads. Deliberately NOT the server's own sentence: this back end answers
 * validation refusals with ASP.NET `ValidationProblemDetails` carrying developer English
 * ("EmailTitle contains invalid locale keys. Valid formats: 'en', 'fr-CA'…"), which is jargon to
 * the person configuring a cycle. The step says what happened to THEIR data instead.
 */
export const getDeadlinesSaveFailureMessageKey = (
    outcome: DeadlinesSaveOutcomeValue,
): TranslationKey | undefined => {
    switch (outcome) {
        case DeadlinesSaveOutcome.PARTIALLY_SAVED:
            return 'cycleEvaluationDeadlines.saving.partiallySaved';
        case DeadlinesSaveOutcome.NOT_SAVED:
            return 'cycleEvaluationDeadlines.saving.notSaved';
        default:
            return undefined;
    }
};
